import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { FinanceAccountNumberResource } from '@/types/resources';
import { api } from '@/utils/api';

import AccountNumberFormModal from './AccountNumberFormModal';

interface Props {
  accountNumber: FinanceAccountNumberResource;
}

const AccountNumbersTableRow: FC<Props> = ({ accountNumber }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this account number?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/finance-account-numbers/${accountNumber.id}`,
      );

      await queryClient.invalidateQueries(['finance-account-numbers'], {
        exact: false,
      });

      alerts.success('Account number was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <tr>
        <Td>{accountNumber.name}</Td>
        <Td>{accountNumber.value}</Td>
        <Td>{accountNumber.important ? 'Yes' : 'No'}</Td>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
      <AnimatePresence>
        {showEditModal && (
          <AccountNumberFormModal
            accountNumber={accountNumber}
            onClose={() => {
              setShowEditModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default AccountNumbersTableRow;
